import { readEffectiveClientId } from '../workspace/storage.js';
import { API_BASE } from './apiBase.js';

// Shared low-level HTTP client factory used by every module's own API wrapper
// (e.g. modules/project-management/pmApi.js, modules/taxonomy-generator/txApi.js,
// modules/dashboard-demo/api.js). Handles base URL resolution, optional Bearer-token
// auth injection, JSON encoding/decoding, and consistent error handling — the
// plumbing that used to be reimplemented three different ways across the app.

// Empty means same-origin. SWA links /api to this environment's Function App, and the Vite
// development proxy does the same locally. Falling back to production would make an omitted
// build variable capable of sending test or developer writes to the live database.
const DEFAULT_BASE = API_BASE;

export class ApiError extends Error {
  constructor(message, status, detail) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

function safeJson(text) {
  try { return JSON.parse(text); } catch { return text; }
}

/**
 * @param {object}  [opts]
 * @param {string}  [opts.baseUrl]  Absolute base URL, or '' for relative /api/* requests
 *                                  (e.g. behind a Vite dev proxy or an SWA-linked backend).
 * @param {boolean} [opts.auth]     If true, every request sends `Authorization: Bearer <token>`
 *                                  read from localStorage.
 * @param {string}  [opts.tokenKey] localStorage key holding the auth token. Default 'mp_token'.
 */
export function createApiClient({ baseUrl = DEFAULT_BASE, auth = false, tokenKey = 'mp_token' } = {}) {
  const base = baseUrl.replace(/\/$/, '');

  function headers(hasBody) {
    const h = {};
    if (hasBody) h['Content-Type'] = 'application/json';
    if (auth) {
      h.Authorization = `Bearer ${localStorage.getItem(tokenKey) || ''}`;
      // The active workspace, so a scoped endpoint does not need it threaded through every
      // call site. An explicit ?client_id= still wins server-side; this is the fallback.
      //
      // Read at request time, like the token, so switching workspace takes effect on the next
      // request without re-creating any client. Validated first because localStorage is
      // user-writable and a value with a newline in it throws inside fetch(), which would
      // break every request in the app rather than just the scoped ones.
      // The EFFECTIVE workspace, not the user's preferred one: a client can be enabled for
      // one module and not another, so the header has to carry what the module now open can
      // actually use.
      const cid = readEffectiveClientId();
      if (cid) h['X-Orbit-Client'] = cid;
    }
    return h;
  }

  function buildUrl(path, params) {
    const cleanPath = path.replace(/^\//, '');
    const url = new URL(`${base}/${cleanPath}`, window.location.origin);
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
      });
    }
    return url;
  }

  // `keepalive` lets a request outlive its page — the one honest way to finish a write
  // (e.g. a deferred delete) while the tab is closing. Browsers cap keepalive bodies at
  // ~64KB, so reserve it for small, final writes.
  async function request(path, { method = 'GET', body, params, signal, keepalive } = {}) {
    const url = buildUrl(path, params);
    const res = await fetch(url.toString(), {
      method,
      headers: headers(body !== undefined),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
      keepalive,
    });
    const text = await res.text();
    const data = text ? safeJson(text) : null;
    if (!res.ok) {
      const detail = (data && (data.detail || data.error)) || text || `HTTP ${res.status}`;
      throw new ApiError(detail, res.status, detail);
    }
    return data;
  }

  /** Revalidate an authenticated JSON representation and surface 304 without parsing a body. */
  async function conditional(path, { method = 'GET', body, params, signal, etag } = {}) {
    const url = buildUrl(path, params);
    const requestHeaders = headers(body !== undefined);
    if (etag) requestHeaders['If-None-Match'] = etag;
    const res = await fetch(url.toString(), {
      method, headers: requestHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined, signal,
    });
    if (res.status === 304) return { notModified: true, etag: etag || null, data: null };
    const text = await res.text();
    const data = text ? safeJson(text) : null;
    if (!res.ok) {
      const detail = (data && (data.detail || data.error)) || text || `HTTP ${res.status}`;
      throw new ApiError(detail, res.status, detail);
    }
    return { notModified: false, etag: res.headers.get('ETag'), data };
  }

  async function download(path, { params } = {}) {
    const url = buildUrl(path, params);
    const res = await fetch(url.toString(), { headers: headers(false) });
    if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status);
    return res.blob();
  }

  // Like `download`, but POSTs a JSON body — for requests whose input is too big
  // for a query string (e.g. an arbitrary list of tables to export).
  // Resolves to { blob, headers } so callers can read response metadata; note the
  // server must set Access-Control-Expose-Headers for any header read here.
  async function downloadPost(path, body, { params } = {}) {
    const url = buildUrl(path, params);
    const res = await fetch(url.toString(), {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      const data = text ? safeJson(text) : null;
      const detail = (data && (data.detail || data.error)) || text || `HTTP ${res.status}`;
      throw new ApiError(detail, res.status, detail);
    }
    return { blob: await res.blob(), headers: res.headers };
  }

  return {
    get:      (path, opts)       => request(path, { ...opts, method: 'GET' }),
    post:     (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
    patch:    (path, body, opts) => request(path, { ...opts, method: 'PATCH', body }),
    put:      (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
    del:      (path, opts)       => request(path, { ...opts, method: 'DELETE' }),
    request,
    conditional,
    download,
    downloadPost,
  };
}
